// ProgressEvent — 19BO+ Line 2 B4.4 · learning-progress cloud sync (field-level event log)
//
// 为什么是字段级行表而非 opaque blob（§13.4(a) 陛下 2026-07-01 裁定）:
//   opaque blob（整 CourseProgress JSON 写一列）是多设备并发写的最差选择——
//   后写者整对象覆盖前者，A 做题时 B 复习闪卡，B 的整对象覆盖 A 的错题记录。
//   字段级行表让每个 wrongAnswer/srs/quizHistory 变更成为独立的一行（client-minted
//   event_id + INSERT OR IGNORE 幂等），多设备并发写在字段+item 粒度上自然合流，
//   冲突最小化。代价是 merge 逻辑更复杂（步骤 5 hydration 做），但数据完整性收益
//   对得起这个复杂度——学习数据是用户长期资产，丢不得。
//
// 字段枚举来源（§13.4 决策矩阵）:
//   - wrongAnswers / srs / quizHistory / bookmarks / subjectiveAttempts:
//     §13.4 原定 user_scoped_cloud（跨设备诊断/复习需要完整历史）
//   - essayDrafts / freeResponseDrafts: 陛下 2026-07-01 扩展云化（用户想跨设备续写）
//   - diagnosis / diagnosisPending: §13.4 cloud（LWW by verdict.diagnosedAt · schema 已定）
//   - activeQuizSession: 陛下 2026-07-01 决策 local 优先 + opt-in 可选云同步
//   - itemOverrides / itemFeedback / repairs: §13.4 保持 local_only（设备私有编辑/反馈/修复）
//     → 不在本枚举内，前端 enqueue 时跳过。
//
// 幂等契约:
//   eventId = client crypto.randomUUID()。前端每次 updateProgress 的 diff emit 一批
//   events，每个带唯一 eventId。重复上传（网络重试 / 多设备同步）时 INSERT OR IGNORE
//   按 PK 静默跳过，changes=0。这是多设备并发写的冲突解决基线——不丢不重。
//
// payload opaqueness:
//   payload 是 opaque JSON（passthrough）。各 field 的 entry 形状由 course-progress.ts
//   各自的 schema 定义（WrongAnswerEntry / SrsEntry / ...）。event 层只负责传输，
//   不校验内部结构——校验在步骤 5 merge 阶段做（safeParse 失败的 entry 丢弃但记 log）。
//   这样 event schema 不会因为某个 entry 字段调整而 break 已存的云数据。
import { z } from 'zod';
/**
 * CourseProgress 中纳入云同步的字段枚举。
 *
 * 不在此列表的字段（itemOverrides / itemFeedback / repairs）保持 local_only
 * （§13.4 · 设备私有编辑/反馈/修复 · 云化隐私顾虑 + 价值低）。
 */
export const PROGRESS_SYNC_FIELDS = [
    'wrongAnswers',
    'srs',
    'quizHistory',
    'bookmarks',
    'subjectiveAttempts',
    'essayDrafts',
    'freeResponseDrafts',
    'diagnosis',
    'diagnosisPending',
    'activeQuizSession',
];
const ProgressFieldSchema = z.enum(PROGRESS_SYNC_FIELDS);
/**
 * 单条 learning-progress 变更事件。对应 progress_events 表一行。
 *
 * eventId: client-minted uuid。INSERT OR IGNORE 的幂等键——重复上传静默跳过。
 * userId / courseId: user-scoped cloud table 的归属键（无 FK · app 强制 · FakeD1 compat）。
 * field: CourseProgress 的哪个字段变了（枚举见 PROGRESS_SYNC_FIELDS）。
 * itemKey: record 的 key（如 mcq_001）。course-level 字段（diagnosis）为 null。
 *          数组型 quizHistory 用 session id。这是 per-item LWW merge 的粒度键。
 * payload: 变更后的完整 entry（opaque JSON · passthrough）。
 *          合并时按 clientOccurredAt 取较新者（per-item-key LWW）。
 * clientOccurredAt: 客户端写 IDB 时的时间戳（ms epoch）。LWW 排序依据。
 *                   不用 server 时间——客户端可能离线写，server 收到时已晚。
 */
export const ProgressEventSchema = z.object({
    eventId: z.string().min(1).max(64),
    userId: z.string().min(1),
    courseId: z.string().min(1),
    field: ProgressFieldSchema,
    itemKey: z.string().nullable(),
    payload: z.record(z.unknown()),
    clientOccurredAt: z.number().int().nonnegative(),
}).passthrough();
//# sourceMappingURL=progress-event.js.map